import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  MapPin,
  Calendar,
  Clock,
  Monitor,
  DoorOpen,
  Car,
  ChevronLeft,
  ChevronRight,
  User,
  Building2,
  Layers,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface Location {
  id: string
  name: string
  address: string | null
  isActive: boolean
}

interface Floor {
  id: string
  locationId: string
  name: string
  level: number
  isActive: boolean
}

interface Space {
  id: string
  floorId: string
  name: string
  type: 'desk' | 'hot_desk' | 'room' | 'meeting_room' | 'phone_booth' | 'locker' | 'parking'
  parkingType: string | null
  capacity: number
  amenities: string[]
  isActive: boolean
  description: string | null
}

interface Booking {
  id: string
  spaceId: string
  userId: string
  title: string | null
  startTime: string
  endTime: string
  status: 'pending' | 'confirmed' | 'checked_in' | 'completed' | 'cancelled' | 'no_show'
  notes: string | null
  space?: Space
  createdAt: string
}

// ============================================
// Helpers
// ============================================

const spaceTypeConfig: Record<string, { label: string; icon: typeof Monitor; color: string; bgColor: string }> = {
  desk: { label: 'Desk', icon: Monitor, color: 'text-blue-500', bgColor: 'bg-blue-50 dark:bg-blue-950' },
  hot_desk: { label: 'Hot Desk', icon: Monitor, color: 'text-blue-400', bgColor: 'bg-blue-50 dark:bg-blue-950' },
  room: { label: 'Room', icon: DoorOpen, color: 'text-green-500', bgColor: 'bg-green-50 dark:bg-green-950' },
  meeting_room: { label: 'Meeting Room', icon: DoorOpen, color: 'text-green-500', bgColor: 'bg-green-50 dark:bg-green-950' },
  phone_booth: { label: 'Phone Booth', icon: DoorOpen, color: 'text-green-400', bgColor: 'bg-green-50 dark:bg-green-950' },
  locker: { label: 'Locker', icon: Monitor, color: 'text-gray-500', bgColor: 'bg-gray-50 dark:bg-gray-950' },
  parking: { label: 'Parking', icon: Car, color: 'text-yellow-500', bgColor: 'bg-yellow-50 dark:bg-yellow-950' },
}

const bookingStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pending', variant: 'secondary' },
  confirmed: { label: 'Confirmed', variant: 'default' },
  checked_in: { label: 'Checked In', variant: 'default' },
  completed: { label: 'Completed', variant: 'outline' },
  cancelled: { label: 'Cancelled', variant: 'destructive' },
  no_show: { label: 'No Show', variant: 'destructive' },
}

function getSpaceBadgeColor(type: string): string {
  if (type.includes('desk')) return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
  if (type.includes('room') || type === 'phone_booth') return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
  if (type === 'parking') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
  return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
}

function getWeekDates(baseDate: Date): Date[] {
  const dates: Date[] = []
  const dayOfWeek = baseDate.getDay()
  const monday = new Date(baseDate)
  monday.setDate(baseDate.getDate() - ((dayOfWeek + 6) % 7))
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    dates.push(d)
  }
  return dates
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatWeekLabel(dates: Date[]): string {
  const first = dates[0]
  const last = dates[6]
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  return `${first.toLocaleDateString(undefined, opts)} - ${last.toLocaleDateString(undefined, opts)}, ${last.getFullYear()}`
}

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// ============================================
// Booking Form Dialog
// ============================================

function BookingFormDialog({
  spaces,
  preSelectedSpace,
  preSelectedDate,
}: {
  spaces: Space[]
  preSelectedSpace?: string
  preSelectedDate?: string
}) {
  const [open, setOpen] = useState(false)
  const [spaceId, setSpaceId] = useState(preSelectedSpace || '')
  const [title, setTitle] = useState('')
  const [date, setDate] = useState(preSelectedDate || formatDate(new Date()))
  const [startTime, setStartTime] = useState('09:00')
  const [endTime, setEndTime] = useState('10:00')
  const [notes, setNotes] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/spaces/bookings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] })
      toast({ title: 'Booking created' })
      setOpen(false)
      resetForm()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create booking. There may be a conflict.', variant: 'destructive' }),
  })

  const resetForm = () => {
    setSpaceId(preSelectedSpace || '')
    setTitle('')
    setDate(formatDate(new Date()))
    setStartTime('09:00')
    setEndTime('10:00')
    setNotes('')
  }

  const handleSubmit = () => {
    const startDateTime = new Date(`${date}T${startTime}:00`).toISOString()
    const endDateTime = new Date(`${date}T${endTime}:00`).toISOString()
    createMutation.mutate({
      spaceId,
      title: title || undefined,
      startTime: startDateTime,
      endTime: endDateTime,
      notes: notes || undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> Book Space</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Book a Space</DialogTitle>
          <DialogDescription>Reserve a desk, room, or parking spot.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Space</Label>
            <Select value={spaceId} onValueChange={setSpaceId}>
              <SelectTrigger><SelectValue placeholder="Select a space" /></SelectTrigger>
              <SelectContent>
                {spaces.filter((s) => s.isActive).map((space) => {
                  const config = spaceTypeConfig[space.type]
                  return (
                    <SelectItem key={space.id} value={space.id}>
                      {space.name} ({config?.label || space.type})
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Title (optional)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Team standup" />
          </div>
          <div className="space-y-2">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any special requirements..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!spaceId || createMutation.isPending}>
            {createMutation.isPending ? 'Booking...' : 'Book'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Weekly Calendar View
// ============================================

function WeeklyCalendar({ bookings, weekDates }: { bookings: Booking[]; weekDates: Date[] }) {
  const hours = Array.from({ length: 12 }, (_, i) => i + 7) // 7 AM to 6 PM

  const getBookingsForDay = useCallback((date: Date) => {
    const dateStr = formatDate(date)
    return bookings.filter((b) => {
      const bookingDate = new Date(b.startTime).toISOString().split('T')[0]
      return bookingDate === dateStr && b.status !== 'cancelled'
    })
  }, [bookings])

  const today = formatDate(new Date())

  return (
    <div className="border rounded-md overflow-auto">
      <div className="min-w-[800px]">
        {/* Header */}
        <div className="grid grid-cols-[80px_repeat(7,1fr)] border-b bg-muted/30">
          <div className="p-2 text-xs font-medium text-muted-foreground text-center">Time</div>
          {weekDates.map((date, i) => {
            const isToday = formatDate(date) === today
            return (
              <div key={i} className={`p-2 text-center border-l ${isToday ? 'bg-primary/5' : ''}`}>
                <div className="text-xs font-medium text-muted-foreground">{dayNames[i]}</div>
                <div className={`text-sm font-bold ${isToday ? 'text-primary' : ''}`}>
                  {date.getDate()}
                </div>
              </div>
            )
          })}
        </div>

        {/* Time slots */}
        {hours.map((hour) => (
          <div key={hour} className="grid grid-cols-[80px_repeat(7,1fr)] border-b last:border-b-0">
            <div className="p-2 text-xs text-muted-foreground text-right pr-3 border-r">
              {`${hour.toString().padStart(2, '0')}:00`}
            </div>
            {weekDates.map((date, dayIdx) => {
              const dayBookings = getBookingsForDay(date).filter((b) => {
                const startHour = new Date(b.startTime).getHours()
                return startHour === hour
              })
              const isToday = formatDate(date) === today
              return (
                <div key={dayIdx} className={`p-1 border-l min-h-[48px] ${isToday ? 'bg-primary/5' : ''}`}>
                  {dayBookings.map((booking) => {
                    const spaceType = booking.space?.type || 'desk'
                    const colorClass = getSpaceBadgeColor(spaceType)
                    return (
                      <div key={booking.id} className={`text-xs p-1 rounded mb-1 truncate ${colorClass}`}>
                        <div className="font-medium truncate">{booking.title || booking.space?.name || 'Booking'}</div>
                        <div className="opacity-70">{formatTime(booking.startTime)} - {formatTime(booking.endTime)}</div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================
// Available Spaces Grid
// ============================================

function AvailableSpacesGrid({
  spaces,
  onSelectSpace,
}: {
  spaces: Space[]
  onSelectSpace: (spaceId: string) => void
}) {
  const grouped = useMemo(() => {
    const groups: Record<string, Space[]> = {}
    for (const space of spaces.filter((s) => s.isActive)) {
      const type = space.type
      if (!groups[type]) groups[type] = []
      groups[type].push(space)
    }
    return groups
  }, [spaces])

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([type, typeSpaces]) => {
        const config = spaceTypeConfig[type]
        const Icon = config?.icon || Monitor
        return (
          <div key={type}>
            <h3 className="font-medium text-sm mb-2 flex items-center gap-2">
              <Icon className={`h-4 w-4 ${config?.color || ''}`} />
              {config?.label || type} ({typeSpaces.length})
            </h3>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {typeSpaces.map((space) => (
                <Card
                  key={space.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${config?.bgColor || ''}`}
                  onClick={() => onSelectSpace(space.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{space.name}</div>
                        <div className="text-xs text-muted-foreground">
                          Capacity: {space.capacity}
                          {space.parkingType && ` | ${space.parkingType.replace('_', ' ')}`}
                        </div>
                      </div>
                      <Badge className={getSpaceBadgeColor(type)} variant="secondary">
                        {config?.label || type}
                      </Badge>
                    </div>
                    {space.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {space.amenities.slice(0, 3).map((amenity) => (
                          <Badge key={amenity} variant="outline" className="text-xs">{amenity}</Badge>
                        ))}
                        {space.amenities.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{space.amenities.length - 3}</Badge>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )
      })}
      {Object.keys(grouped).length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center py-12">
            <MapPin className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No spaces available</p>
            <p className="text-sm text-muted-foreground mt-1">Select a location and floor to see available spaces</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ============================================
// My Bookings
// ============================================

function MyBookingsList() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['my-bookings'],
    queryFn: () => apiClient.get<Booking[]>('/spaces/bookings/my', { upcoming: 'true' }),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/spaces/bookings/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['space-bookings'] })
      toast({ title: 'Booking cancelled' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to cancel booking', variant: 'destructive' }),
  })

  const bookings: Booking[] = data?.data ?? []

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    )
  }

  if (bookings.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-8">
          <Calendar className="h-10 w-10 mb-3 opacity-50 text-muted-foreground" />
          <p className="text-muted-foreground text-sm">No upcoming bookings</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-2">
      {bookings.map((booking) => {
        const spaceType = booking.space?.type || 'desk'
        const config = spaceTypeConfig[spaceType]
        const Icon = config?.icon || Monitor
        const statusConf = bookingStatusConfig[booking.status]
        return (
          <Card key={booking.id}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-md ${config?.bgColor || 'bg-muted'}`}>
                    <Icon className={`h-4 w-4 ${config?.color || ''}`} />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{booking.title || booking.space?.name || 'Booking'}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(booking.startTime).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={statusConf?.variant || 'secondary'}>
                    {statusConf?.label || booking.status}
                  </Badge>
                  {(booking.status === 'pending' || booking.status === 'confirmed') && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => cancelMutation.mutate(booking.id)}>
                      <X className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

// ============================================
// Main Page
// ============================================

export function SpaceBookingPage() {
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [selectedFloor, setSelectedFloor] = useState<string>('')
  const [weekOffset, setWeekOffset] = useState(0)
  const [activeTab, setActiveTab] = useState('calendar')

  const baseDate = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + weekOffset * 7)
    return d
  }, [weekOffset])

  const weekDates = useMemo(() => getWeekDates(baseDate), [baseDate])

  // Fetch locations
  const { data: locationsData } = useQuery({
    queryKey: ['space-locations'],
    queryFn: () => apiClient.get<{ data: Location[]; meta: unknown }>('/spaces/locations', { limit: 100 }),
  })

  const locations: Location[] = locationsData?.data ?? (locationsData as any)?.data?.data ?? []

  // Fetch floors for selected location
  const { data: floorsData } = useQuery({
    queryKey: ['space-floors', selectedLocation],
    queryFn: () => apiClient.get<Floor[]>(`/spaces/locations/${selectedLocation}/floors`),
    enabled: !!selectedLocation,
  })

  const floors: Floor[] = floorsData?.data ?? []

  // Fetch spaces
  const { data: spacesData, isLoading: spacesLoading } = useQuery({
    queryKey: ['space-list', selectedLocation, selectedFloor],
    queryFn: () => apiClient.get<{ data: Space[]; meta: unknown }>('/spaces', {
      limit: 100,
      ...(selectedLocation ? { locationId: selectedLocation } : {}),
      ...(selectedFloor ? { floorId: selectedFloor } : {}),
    }),
  })

  const spaces: Space[] = spacesData?.data ?? (spacesData as any)?.data?.data ?? []

  // Fetch bookings for the week
  const { data: bookingsData, isLoading: bookingsLoading } = useQuery({
    queryKey: ['space-bookings', selectedLocation, weekDates[0]?.toISOString(), weekDates[6]?.toISOString()],
    queryFn: () => apiClient.get<{ data: Booking[]; meta: unknown }>('/spaces/bookings', {
      limit: 200,
      startDate: weekDates[0].toISOString(),
      endDate: new Date(weekDates[6].getTime() + 86400000).toISOString(),
      ...(selectedLocation ? { locationId: selectedLocation } : {}),
    }),
  })

  const bookings: Booking[] = bookingsData?.data ?? (bookingsData as any)?.data?.data ?? []

  // Fetch space stats
  const { data: _statsData } = useQuery({
    queryKey: ['space-stats'],
    queryFn: () => apiClient.get<{ totalSpaces: number; byType: Record<string, number> }>('/spaces/stats'),
  })

  // Auto-select first location
  if (locations.length > 0 && !selectedLocation) {
    setSelectedLocation(locations[0].id)
  }

  // Auto-select first floor when location changes
  if (floors.length > 0 && !selectedFloor) {
    setSelectedFloor(floors[0].id)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Space Booking</h1>
          <p className="text-muted-foreground">Book desks, rooms, and parking spaces</p>
        </div>
        <BookingFormDialog spaces={spaces} />
      </div>

      {/* Location & Floor Selector */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <Select value={selectedLocation} onValueChange={(val) => { setSelectedLocation(val); setSelectedFloor('') }}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {floors.length > 0 && (
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedFloor} onValueChange={setSelectedFloor}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select floor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-floors">All Floors</SelectItem>
                {floors.map((floor) => (
                  <SelectItem key={floor.id} value={floor.id}>
                    {floor.name} (Level {floor.level})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Space type legend */}
        <div className="flex items-center gap-3 ml-auto">
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded-sm bg-blue-500" />
            <span className="text-xs text-muted-foreground">Desk</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded-sm bg-green-500" />
            <span className="text-xs text-muted-foreground">Room</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded-sm bg-yellow-500" />
            <span className="text-xs text-muted-foreground">Parking</span>
          </div>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="calendar">
            <Calendar className="mr-2 h-4 w-4" /> Calendar
          </TabsTrigger>
          <TabsTrigger value="spaces">
            <MapPin className="mr-2 h-4 w-4" /> Available Spaces
          </TabsTrigger>
          <TabsTrigger value="my-bookings">
            <User className="mr-2 h-4 w-4" /> My Bookings
          </TabsTrigger>
        </TabsList>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="mt-4">
          {/* Week Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" onClick={() => setWeekOffset(0)}>Today</Button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setWeekOffset((w) => w - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[200px] text-center">
                {formatWeekLabel(weekDates)}
              </span>
              <Button variant="ghost" size="icon" onClick={() => setWeekOffset((w) => w + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="text-sm text-muted-foreground">
              {bookings.length} booking{bookings.length !== 1 ? 's' : ''} this week
            </div>
          </div>

          {bookingsLoading ? (
            <Skeleton className="h-[500px] w-full" />
          ) : (
            <WeeklyCalendar bookings={bookings} weekDates={weekDates} />
          )}
        </TabsContent>

        {/* Available Spaces Tab */}
        <TabsContent value="spaces" className="mt-4">
          {spacesLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
            </div>
          ) : (
            <AvailableSpacesGrid
              spaces={spaces}
              onSelectSpace={(_spaceId) => {
                // Could open a booking form pre-selecting this space
                // For now we just switch to calendar
              }}
            />
          )}
        </TabsContent>

        {/* My Bookings Tab */}
        <TabsContent value="my-bookings" className="mt-4">
          <div className="max-w-2xl">
            <h3 className="font-medium mb-3">Upcoming Bookings</h3>
            <MyBookingsList />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
